import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["submit", "email", "status"]
  static values = {
    validateOnBlur: Boolean,
    validateUrl: String
  }

  connect() {
    console.log("signup-form controller connected")

    // Set default for validateUrl if not provided
    if (!this.hasValidateUrlValue || !this.validateUrlValue) {
      this.validateUrlValue = "/users/validate_email"
    }

    // Store original button text (works for both input[type=submit] and button)
    if (this.hasSubmitTarget) {
      this.originalButtonText = this.submitTarget.value || this.submitTarget.innerHTML
      console.log("Submit target found, original text:", this.originalButtonText)
    } else {
      console.log("No submit target found")
    }

    this.lastValidatedEmail = null
    this.isValid = false

    // Bind blur validation if enabled (defaults to false if not set)
    if (this.validateOnBlurValue && this.hasEmailTarget) {
      this.boundValidateOnBlur = this.validateOnBlur.bind(this)
      this.boundHandleEnterKey = this.handleEnterKey.bind(this)
      this.emailTarget.addEventListener('blur', this.boundValidateOnBlur)
      this.emailTarget.addEventListener('keypress', this.boundHandleEnterKey)
      console.log("Blur validation enabled")
    }
  }

  disconnect() {
    if (this.hasEmailTarget && this.boundValidateOnBlur) {
      this.emailTarget.removeEventListener('blur', this.boundValidateOnBlur)
      this.emailTarget.removeEventListener('keypress', this.boundHandleEnterKey)
    }
  }

  handleEnterKey(event) {
    if (event.which === 13) {
      event.preventDefault()
      this.emailTarget.blur()
    }
  }

  validateOnBlur() {
    const email = this.emailTarget.value.trim()
    if (email && email !== this.lastValidatedEmail) {
      this.validateEmail(email)
    }
  }

  // Called on form submit (for demo modal flow)
  validate(event) {
    console.log("validate() called on form submit")
    this.showValidatingState()
    // Don't prevent default - let the form submit normally
  }

  // Async email validation via server
  async validateEmail(email) {
    console.log("validateEmail() called for:", email)
    this.showValidatingState()
    this.clearStatus()

    try {
      const response = await fetch(this.validateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: `email=${encodeURIComponent(email)}`
      })

      const data = await response.json()
      this.lastValidatedEmail = email
      this.handleValidationResult(data)
    } catch (error) {
      console.log("Email validation request failed:", error)
      // On error, allow form submission - server will validate again
      this.resetButton()
      this.enableEmailField()
    }
  }

  handleValidationResult(data) {
    console.log("Validation result:", data)
    this.enableEmailField()

    if (data.error) {
      this.showError(data.error)
      this.isValid = false
      this.resetButton()
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = true
      }
      return
    }

    if (data.is_valid) {
      this.isValid = true
      if (data.did_you_mean) {
        this.showWarning(`Did you mean <em>${data.did_you_mean}</em>?`)
      }
      this.resetButton()
    } else {
      this.isValid = false
      if (data.did_you_mean) {
        this.showError(`Did you mean <em>${data.did_you_mean}</em>?`)
      } else {
        this.showError('<span class="fa fa-warning mr-1"></span>Email address is invalid.')
      }
      this.resetButton()
      if (this.hasSubmitTarget) {
        this.submitTarget.disabled = true
      }
    }
  }

  showValidatingState() {
    console.log("showValidatingState() called, hasSubmitTarget:", this.hasSubmitTarget)
    if (!this.hasSubmitTarget) return

    const spinner = '<span class="fas fa-spinner fa-spin mr-1"></span> Validating...'
    if (this.submitTarget.tagName === 'INPUT') {
      this.submitTarget.value = "Validating..."
    } else {
      this.submitTarget.innerHTML = spinner
    }
    this.submitTarget.disabled = true

    if (this.hasEmailTarget) {
      this.emailTarget.readOnly = true
      this.emailTarget.classList.add("bg-light")
    }
  }

  resetButton() {
    if (!this.hasSubmitTarget) return

    if (this.submitTarget.tagName === 'INPUT') {
      this.submitTarget.value = this.originalButtonText
    } else {
      this.submitTarget.innerHTML = this.originalButtonText
    }
    this.submitTarget.disabled = false
  }

  enableEmailField() {
    if (this.hasEmailTarget) {
      this.emailTarget.readOnly = false
      this.emailTarget.classList.remove("bg-light")
    }
  }

  clearStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = ""
    }
  }

  showError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `<div class="email-error">${message}</div>`
    }
  }

  showWarning(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `<div class="email-warning">${message}</div>`
    }
  }

  useSuggestion(event) {
    event.preventDefault()
    const suggestion = event.currentTarget.dataset.suggestion
    if (suggestion && this.hasEmailTarget) {
      this.emailTarget.value = suggestion
      // Clear the error
      const alert = this.element.querySelector('.alert-danger')
      if (alert) alert.remove()
      this.clearStatus()
      // Re-validate with new email
      this.validateEmail(suggestion)
    }
  }
}
